import b from 'b_';
import { DeferredRenderer, Spin } from 'platform-components';
import React, { useEffect } from 'react';
import { connect } from 'react-redux';
import { useParams } from 'react-router';
import { Action, bindActionCreators, Dispatch } from 'redux';

import { CustomLessonReadonlyViewContainer } from 'common.components/lesson-view.custom-readonly-view/custom-lesson-readonly-view.container';
import { CustomLessonViewContainer } from 'common.components/lesson-view.custom-view/custom-lesson-view.container';
import ProblemView from 'common.components/lesson-view.problem-view/problem-view';
import Problems from 'common.components/lesson-view.problems/problems';
import * as lessonViewActions from 'common.components/lesson-view.store/actions';
import * as lessonViewSelectors from 'common.components/lesson-view.store/selectors';
import { LESSON_MODAL_TABS, LESSON_CONTENT_MODE } from 'common.components/lesson-view.store/typings';
import StudentResultView from 'common.components/lesson-view.student-result-view/student-result-view';
import * as lessonSelectors from 'common.components/lesson/selectors';
import * as resultsSelectors from 'common.components/results/selectors';
import { getIsTeacherFromMes } from 'utils/external-lms/external-lms';

import { withRouterPath, type InjectedProps as WithRouterPathProps } from 'platform-components/hocs';
import AddProblemButton from '../__add-problem-button/lesson-view__add-problem-button';
import LessonBanner from '../__banner/lesson-view__banner';
import LessonDescription from '../__description/lesson-view__description';
import EmptyState from '../__empty-state/lesson-view__empty-state';
import LessonName from '../__lesson-name/lesson-view__lesson-name';
import { LookAsStudentButton } from '../__look-as-student-button/lesson-view__look-as-student-button';
import LessonViewMeta from '../__meta/lesson-view__meta';
import { PutOnBoardButton } from '../__put-on-board-button/lesson-view__put-on-board-button';
import { LessonViewResultsWithInformationBanners } from '../__results/lesson-view__results';
import SandboxStub from '../__sandbox-stub/lesson-view__sandbox-stub';
import Skeleton from '../__skeleton/lesson-view__skeleton';
import Tabs from '../__tabs/lesson-view__tabs';

interface OwnProps extends WithRouterPathProps {
    isPutOnBoardEnabled?: boolean;
    shouldShowResultsStub?: boolean;
    hideBanners?: boolean;
}

enum LESSON_CONTENT_THEME {
    WHITE = 'white',
    GREY = 'grey',
}

type StoreProps = ReturnType<typeof mapStateToProps>;
type DispatchProps = ReturnType<typeof mapDispatchToProps>;

export interface Props extends OwnProps, StoreProps, DispatchProps {}

// eslint-disable-next-line complexity
export const LessonViewContent = (props: Props) => {
    const {
        isStatsExpanded,
        setSelectedTab,
        isPutOnBoardEnabled,
        isLessonContentEditable,
        mode,
        selectedTab,
        shouldShowBanner,
        shouldShowResultsStub,
        hideBanners,
        isTeacherFromMes,
    } = props;

    const params = useParams<Record<string, string>>();

    useEffect(() => {
        if (shouldShowResultsStub) {
            setSelectedTab(LESSON_MODAL_TABS.PROBLEMS);
        } else {
            setSelectedTab(LESSON_MODAL_TABS.STATS);
        }
    }, [setSelectedTab, shouldShowResultsStub]);

    const renderResults = () => {
        if (shouldShowResultsStub) {
            return <SandboxStub />;
        }

        return <LessonViewResultsWithInformationBanners hideBanners={hideBanners} />;
    };

    switch (mode) {
        case LESSON_CONTENT_MODE.LOADING:
            return (
                <div className="lesson-view__content">
                    <Spin center />
                </div>
            );
        case LESSON_CONTENT_MODE.COPY:
            return (
                <div className="lesson-view__content">
                    <Skeleton />
                </div>
            );
        case LESSON_CONTENT_MODE.STREAM:
            return (
                <div className="lesson-view__content">
                    <LessonName canEdit={false} />
                    <LessonViewMeta />
                </div>
            );

        case LESSON_CONTENT_MODE.READONLY:
            return (
                <div className={b('lesson-view', 'content', { expanded: isStatsExpanded })}>
                    {shouldShowBanner ? (
                        <>
                            <LessonBanner />
                            <LessonName canEdit={false} />
                            <LessonViewMeta />
                        </>
                    ) : (
                        <>
                            <LessonName canEdit={false} />
                            <LessonDescription canEdit={false} />
                            <LessonViewMeta />
                        </>
                    )}
                    <div className="lesson-view__content-controls">
                        <Tabs />
                        <PutOnBoardButton disabled={!isPutOnBoardEnabled} shouldShowTooltip={!isPutOnBoardEnabled} />
                        <LookAsStudentButton disabled={!isPutOnBoardEnabled} shouldShowTooltip={!isPutOnBoardEnabled} />
                    </div>
                    {selectedTab === LESSON_MODAL_TABS.STATS && renderResults()}
                    {selectedTab === LESSON_MODAL_TABS.PROBLEMS && (
                        <DeferredRenderer>
                            <Problems canEdit={false} mix="lesson-view__problems" />
                        </DeferredRenderer>
                    )}
                </div>
            );

        case LESSON_CONTENT_MODE.EDIT_EMPTY:
            return (
                <div className="lesson-view__content">
                    <LessonName canEdit={true} />
                    <LessonDescription canEdit={true} />
                    <LessonViewMeta />
                    <EmptyState />
                </div>
            );

        case LESSON_CONTENT_MODE.EDIT:
            const canEdit = Boolean(params?.classId) && isLessonContentEditable && !isTeacherFromMes;

            return (
                <div className="lesson-view__content">
                    {shouldShowBanner ? (
                        <>
                            <LessonBanner />
                            <LessonName canEdit={canEdit} />
                            <LessonViewMeta />
                        </>
                    ) : (
                        <>
                            <LessonName canEdit={canEdit} />
                            <LessonDescription canEdit={canEdit} />
                            <LessonViewMeta />
                        </>
                    )}
                    <div className="lesson-view__content-controls">
                        {canEdit && (
                            <>
                                <AddProblemButton />
                                <PutOnBoardButton
                                    disabled={!isPutOnBoardEnabled}
                                    shouldShowTooltip={!isPutOnBoardEnabled}
                                />
                                <LookAsStudentButton
                                    disabled={!isPutOnBoardEnabled}
                                    shouldShowTooltip={!isPutOnBoardEnabled}
                                />
                            </>
                        )}
                    </div>
                    <Problems canEdit={canEdit} />
                </div>
            );

        case LESSON_CONTENT_MODE.PROBLEM_VIEW:
            return (
                <div
                    className={b('lesson-view', 'content', {
                        'without-padding': true,
                    })}
                >
                    <ProblemView />
                </div>
            );

        case LESSON_CONTENT_MODE.CUSTOM_PROBLEM_VIEW:
            return (
                <div
                    className={b('lesson-view', 'content', {
                        'without-padding': true,
                    })}
                >
                    <ProblemView returnButtonTextId="customLessonView.returnFromProblemPreview" />
                </div>
            );

        case LESSON_CONTENT_MODE.STUDENT_RESULTS:
            return (
                <div
                    className={b('lesson-view', 'content', {
                        'without-padding': true,
                    })}
                >
                    <StudentResultView />
                </div>
            );
        case LESSON_CONTENT_MODE.CUSTOM_VIEW:
            return (
                <div
                    className={b('lesson-view', 'content', {
                        'without-padding': true,
                        theme: LESSON_CONTENT_THEME.WHITE,
                    })}
                >
                    <CustomLessonViewContainer />
                </div>
            );

        case LESSON_CONTENT_MODE.CUSTOM_READONLY_VIEW:
            return (
                <div
                    className={b('lesson-view', 'content', {
                        'without-padding': true,
                        theme: LESSON_CONTENT_THEME.WHITE,
                    })}
                >
                    <CustomLessonReadonlyViewContainer />
                </div>
            );
        default:
            return null;
    }
};

const mapStateToProps = (storeState: any, ownProps: OwnProps) => {
    const {
        location: { search },
    } = ownProps;
    return {
        isLessonContentEditable: lessonSelectors.getIsLessonContentEditable(storeState),
        mode: lessonViewSelectors.getLessonContentMode(storeState),
        selectedTab: lessonViewSelectors.getSelectedTab(storeState),
        shouldShowBanner: lessonSelectors.getShouldShowBanner(storeState),
        isTeacherFromMes: getIsTeacherFromMes(search),
        isStatsExpanded: resultsSelectors.getIsStatsExpanded(storeState),
    };
};

const mapDispatchToProps = (dispatch: Dispatch<Action>) =>
    bindActionCreators(
        {
            setSelectedTab: lessonViewActions.setSelectedTab,
        },
        dispatch
    );

export default withRouterPath(connect(mapStateToProps, mapDispatchToProps)(LessonViewContent));
